import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { healCharacterWound, saveWoundReport, voidCharacterWound } from '../../../../services/wounds.api'
import { useSheetReviewContext } from './SheetReviewContext'
import useCharacterWounds from '../../shared/useCharacterWounds'
import { WOUND_ZONE_LABELS, WOUND_ZONE_ORDER } from '../../shared/woundZones'
import AppContext from '../../../../contexts/AppContext'

const MAX_ZONES = 4
const MAX_TEXT = 200

function formatRemaining(expiresAt) {
  if (!expiresAt) return null
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (!Number.isFinite(ms) || ms <= 0) return 'a punto de sanar'
  const hours = Math.round(ms / 3600000)
  if (hours < 24) return `${Math.max(1, hours)} h`
  return `${Math.round(hours / 24)} d`
}

function formatDate(value) {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

/**
 * Heridas post-combate: estado activo, parte redactado por el jugador y cicatrices.
 * La gravedad la fija el servidor; aquí solo se describen las heridas.
 */
export default function WoundsPanel() {
  const { slug } = useParams()
  const { isOwner } = useSheetReviewContext()
  const { hasPermission } = useContext(AppContext)
  const canStaffClose = !!(
    hasPermission &&
    (hasPermission('adventure.gm') ||
      hasPermission('adventure.moderate') ||
      hasPermission('admin.dashboard'))
  )

  const { active, scars, setState } = useCharacterWounds(slug)
  const [draft, setDraft] = useState([])
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setDraft(active?.zones || [])
  }, [active])

  const usedZones = useMemo(() => new Set(draft.map((z) => z.zone)), [draft])
  const freeZones = WOUND_ZONE_ORDER.filter((z) => !usedZones.has(z))

  const addZone = () => {
    if (!freeZones.length || draft.length >= MAX_ZONES) return
    setDraft([...draft, { zone: freeZones[0], text: '', primary: draft.length === 0 }])
  }

  const patchZone = (index, patch) => {
    setDraft(draft.map((z, i) => (i === index ? { ...z, ...patch } : z)))
  }

  const removeZone = (index) => {
    const next = draft.filter((_, i) => i !== index)
    if (next.length && !next.some((z) => z.primary)) next[0].primary = true
    setDraft(next)
  }

  const setPrimary = (index) => {
    setDraft(draft.map((z, i) => ({ ...z, primary: i === index })))
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const data = await saveWoundReport(slug, draft)
      setState(data)
      setDraft(data?.active?.zones || [])
      setEditing(false)
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo guardar el parte')
    } finally {
      setBusy(false)
    }
  }

  const staffClose = async (kind) => {
    const ok =
      kind === 'void'
        ? window.confirm('¿Anular esta herida sin dejar cicatriz? Es para errores de combate, no para curar.')
        : window.confirm('¿Curar esta herida ahora? Quedará como cicatriz en la ficha.')
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      const data = kind === 'void' ? await voidCharacterWound(slug) : await healCharacterWound(slug)
      setState(data)
      setDraft(data?.active?.zones || [])
      setEditing(false)
    } catch (e) {
      setError(e?.response?.data?.message || (kind === 'void' ? 'No se pudo anular' : 'No se pudo curar'))
    } finally {
      setBusy(false)
    }
  }

  if (!active && !scars.length) return null

  return (
    <article className="char-card char-card--section char-wounds">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">Heridas</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>

      {active ? (
        <div className={`char-wounds__state char-wounds__state--${active.severity}`}>
          <div className="char-wounds__state-head">
            <span className="char-wounds__severity">{active.severityLabel}</span>
            {active.expiresAt ? (
              <span className="char-wounds__timer">Sana en {formatRemaining(active.expiresAt)}</span>
            ) : null}
          </div>

          {active.zones.length ? (
            <ul className="char-wounds__list">
              {active.zones.map((z) => (
                <li key={z.zone} className="char-wounds__item">
                  <span className="char-wounds__zone">{WOUND_ZONE_LABELS[z.zone] || z.zone}</span>
                  <span className="char-wounds__text">{z.text || 'Sin describir'}</span>
                  {z.primary ? <span className="char-wounds__primary">principal</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="char-wounds__empty">
              Sin parte redactado. {isOwner ? 'Describe tus heridas para que se vean en la crónica.' : ''}
            </p>
          )}

          {canStaffClose ? (
            <div className="char-wounds__staff">
              <button
                type="button"
                className="char-btn char-btn--ghost"
                disabled={busy}
                onClick={() => staffClose('heal')}
              >
                Curar (cicatriz)
              </button>
              <button
                type="button"
                className="char-btn char-btn--ghost"
                disabled={busy}
                onClick={() => staffClose('void')}
              >
                Anular (sin cicatriz)
              </button>
            </div>
          ) : null}

          {error && !editing ? <p className="char-wounds__error">{error}</p> : null}

          {isOwner && !editing ? (
            <button type="button" className="char-btn char-btn--ghost" onClick={() => setEditing(true)}>
              {active.zones.length ? 'Editar parte' : 'Redactar parte'}
            </button>
          ) : null}

          {isOwner && editing ? (
            <div className="char-wounds__editor">
              {draft.map((z, i) => (
                <div key={`${z.zone}-${i}`} className="char-wounds__row">
                  <select
                    className="char-wounds__select"
                    value={z.zone}
                    onChange={(e) => patchZone(i, { zone: e.target.value })}
                    aria-label="Zona herida"
                  >
                    {WOUND_ZONE_ORDER.filter((opt) => opt === z.zone || !usedZones.has(opt)).map((opt) => (
                      <option key={opt} value={opt}>
                        {WOUND_ZONE_LABELS[opt]}
                      </option>
                    ))}
                  </select>

                  <input
                    className="char-wounds__input"
                    type="text"
                    maxLength={MAX_TEXT}
                    placeholder="Costillas fisuradas, brazo en cabestrillo…"
                    value={z.text}
                    onChange={(e) => patchZone(i, { text: e.target.value })}
                  />

                  <button
                    type="button"
                    className={`char-wounds__flag${z.primary ? ' is-active' : ''}`}
                    onClick={() => setPrimary(i)}
                    title="Mostrar esta zona en la crónica"
                  >
                    Principal
                  </button>

                  <button type="button" className="char-wounds__remove" onClick={() => removeZone(i)} aria-label="Quitar zona">
                    ×
                  </button>
                </div>
              ))}

              {error ? <p className="char-wounds__error">{error}</p> : null}

              <div className="char-wounds__actions">
                <button
                  type="button"
                  className="char-btn char-btn--ghost"
                  onClick={addZone}
                  disabled={!freeZones.length || draft.length >= MAX_ZONES}
                >
                  Añadir zona
                </button>
                <button type="button" className="char-btn-accent" onClick={save} disabled={busy}>
                  {busy ? 'Guardando…' : 'Guardar parte'}
                </button>
                <button
                  type="button"
                  className="char-btn char-btn--ghost"
                  onClick={() => {
                    setDraft(active.zones || [])
                    setEditing(false)
                    setError(null)
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {scars.length ? (
        <div className="char-wounds__scars">
          <h4 className="char-wounds__scars-title">Cicatrices</h4>
          <ul className="char-wounds__list char-wounds__list--scars">
            {scars.map((scar) => {
              const primary = scar.zones.find((z) => z.primary) || scar.zones[0] || null
              return (
                <li key={scar.woundId} className="char-wounds__item">
                  <span className="char-wounds__zone">
                    {primary ? WOUND_ZONE_LABELS[primary.zone] || primary.zone : scar.severityLabel}
                  </span>
                  <span className="char-wounds__text">{primary?.text || scar.severityLabel}</span>
                  {scar.origin ? (
                    <span className="char-wounds__origin">
                      {scar.origin.kind === 'mission' ? 'Misión: ' : 'Crónica: '}
                      {scar.origin.adventureSlug ? (
                        <Link to={`/aventura/${scar.origin.adventureSlug}`}>{scar.origin.label}</Link>
                      ) : (
                        scar.origin.label
                      )}
                    </span>
                  ) : null}
                  <span className="char-wounds__when">{formatDate(scar.healedAt || scar.createdAt)}</span>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </article>
  )
}
